import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Reveal } from "./Reveal";
import { useLanguage } from "../i18n/LanguageContext";

const API = process.env.REACT_APP_BACKEND_URL;

const NEWS_IMAGES = ["/images/noticia-n1.webp", "/images/noticia-n2.webp", "/images/noticia-n3.webp"];

const DATE_LOCALE = { pt: "pt-PT", de: "de-CH", fr: "fr-CH", it: "it-CH", en: "en-GB" };

const SectionTitle = ({ children, testid }) => (
    <div>
        <div className="flex items-center justify-center gap-4">
            <span className="hidden sm:block h-px flex-1 max-w-40 bg-gradient-to-l from-[#c9a24b]/60 to-transparent" />
            <span className="hidden sm:block w-1.5 h-1.5 rotate-45 border border-[#c9a24b]" />
            <h2 className="font-caps gold-text text-base sm:text-lg tracking-[0.2em] text-center" data-testid={testid}>
                {children}
            </h2>
            <span className="hidden sm:block w-1.5 h-1.5 rotate-45 border border-[#c9a24b]" />
            <span className="hidden sm:block h-px flex-1 max-w-40 bg-gradient-to-r from-[#c9a24b]/60 to-transparent" />
        </div>
        <div className="flex justify-center mt-2">
            <img loading="lazy" decoding="async" src="/images/ornamento.webp" alt="" aria-hidden="true" className="w-36 opacity-90" />
        </div>
    </div>
);

export const CulturaNoticias = () => {
    const { t, lang } = useLanguage();
    const [liveNews, setLiveNews] = useState(null);

    useEffect(() => {
        fetch(`${API}/api/news?limit=3&lang=${lang}`)
            .then((r) => r.json())
            .then((d) => setLiveNews(d.articles || []))
            .catch(() => setLiveNews([]));
    }, [lang]);

    const live = liveNews && liveNews.length ? liveNews.slice(0, 3) : null;

    const fmtDate = (iso) => {
        if (!iso) return "";
        const d = new Date(iso);
        if (Number.isNaN(d.getTime())) return "";
        return new Intl.DateTimeFormat(DATE_LOCALE[lang] || "pt-PT", { day: "numeric", month: "short" }).format(d);
    };
    return (
        <section className="py-12 sm:py-16" data-testid="cultura-noticias-section">
            <div className="mx-auto max-w-[1500px] px-5 sm:px-10">
                <Reveal delay={0.15}>
                    <SectionTitle testid="ultimas-noticias-title">{t.culturaNoticias.noticias}</SectionTitle>
                    {live ? (
                        <>
                            <div className="mt-8 grid sm:grid-cols-3 gap-5" data-testid="noticias-live-grid">
                                {live.map((a, i) => (
                                    <a
                                        key={a.id}
                                        href={a.url}
                                        {...(a.url.startsWith("/") ? {} : { target: "_blank", rel: "noopener noreferrer" })}
                                        className="group border border-[#c9a24b]/20 bg-[#0a0705] hover:border-[#c9a24b]/50 transition-colors duration-500"
                                        data-testid={`noticia-card-${i}`}
                                    >
                                        <div className="overflow-hidden">
                                            <img loading="lazy" decoding="async"
                                                src={a.image || "/images/noticia-fallback.webp"}
                                                alt={a.title}
                                                className="w-full aspect-[2/1] object-cover group-hover:scale-105 transition-transform duration-700"
                                                data-testid={`noticia-image-${i}`}
                                            />
                                        </div>
                                        <div className="p-5">
                                            <h3 className="font-display font-light text-lg text-[#efe6d6]/90 leading-snug line-clamp-2" data-testid={`noticia-title-${i}`}>{a.title}</h3>
                                            <p className="font-caps text-[10px] tracking-[0.25em] text-[#c9a24b] mt-4">
                                                {[a.source, fmtDate(a.published_at)].filter(Boolean).join(" · ")}
                                            </p>
                                        </div>
                                    </a>
                                ))}
                            </div>
                            <div className="text-center mt-6">
                                <Link to="/noticias" data-testid="noticias-ver-todas"
                                    className="font-caps text-[10px] tracking-[0.3em] uppercase text-[#c9a24b] hover:text-[#ecd49b] transition-colors duration-300">
                                    {t.noticias.verTodas} →
                                </Link>
                            </div>
                        </>
                    ) : (
                        <>
                            <div className="mt-8 grid sm:grid-cols-3 gap-5">
                                {t.culturaNoticias.news.map((n, i) => (
                                    <div
                                        key={n.title}
                                        className="group border border-[#c9a24b]/20 bg-[#0a0705] hover:border-[#c9a24b]/50 transition-colors duration-500"
                                        data-testid={`noticia-card-${i}`}
                                    >
                                        <div className="overflow-hidden">
                                            <img loading="lazy" decoding="async"
                                                src={NEWS_IMAGES[i]}
                                                alt={n.title}
                                                className="w-full aspect-[2/1] object-cover group-hover:scale-105 transition-transform duration-700"
                                                data-testid={`noticia-image-${i}`}
                                            />
                                        </div>
                                        <div className="p-5">
                                            <h3 className="font-display font-light text-lg text-[#efe6d6]/90 leading-snug">{n.title}</h3>
                                            <p className="font-caps text-[10px] tracking-[0.25em] text-[#c9a24b] mt-4">{n.date}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <p className="text-center text-xs font-light text-[#efe6d6]/40 mt-6 tracking-wide">
                                {t.culturaNoticias.nota}
                            </p>
                        </>
                    )}
                </Reveal>
            </div>
        </section>
    );
};
